import { createSelector } from '@reduxjs/toolkit';
import { EDistributorSource } from 'core/model/enums/checkout.enum';
import { ECustomerSource } from 'core/model/enums/customer.enum';
import { ICheckoutDetailGroup, TDistributorSource } from 'core/model/interfaces/checkout.interface';
import { ICreditLimit } from 'core/model/interfaces/credits.interface';
import { IPaymentType } from 'core/model/interfaces/payment-type.interface';
import {
  authCustomerIsHybridSelector,
  authGetCustomerSource,
  selectCustomerLegalConsent,
} from 'core/store/auth/selectors';
import {
  canBuyByDistributor,
  extractCurrency,
  hasCreditInsufficiency,
  hasProductsInGroupingRules,
} from '../checkout-state.utils';
import {
  checkoutDetailSelector,
  paymentMethodSelector,
  selectCheckoutAgreements,
  sourceInViewSelector,
} from './core.selectors';
import { canBuySelector, creditLimitsBySourceSelector } from './credits.selectors';
import { paymentMethodsBySourceSelector } from './payment.selectors';

export const hasProductsOfSelector = createSelector(checkoutDetailSelector, detail => ({
  alicorpCart: hasProductsInGroupingRules(detail.alicorpCart),
  alliedCart: hasProductsInGroupingRules(detail.alliedCart),
  generalCart: hasProductsInGroupingRules(detail.generalCart),
}));

export const checkoutHasProductsBySourceSelector = createSelector(
  authCustomerIsHybridSelector,
  hasProductsOfSelector,
  sourceInViewSelector,
  (isHybrid, hasProductsOf, sourceInView) => {
    if (isHybrid) {
      return sourceInView === ECustomerSource.DEX ? hasProductsOf.alicorpCart : hasProductsOf.alliedCart;
    }
    return hasProductsOf.generalCart;
  },
);

export const checkoutFullDetailSelector = createSelector(
  authGetCustomerSource,
  checkoutDetailSelector,
  paymentMethodsBySourceSelector,
  creditLimitsBySourceSelector,
  sourceInViewSelector,
  (source, detail, paymentMethodsBySource, creditLimits, sourceInView = ECustomerSource.DEX) => {
    const { apudex, dex } = paymentMethodsBySource;
    let paymentMethods: Array<IPaymentType> | undefined = [];
    let selectedDetail: ICheckoutDetailGroup = detail.generalCart;
    let creditLimit: ICreditLimit = creditLimits[ECustomerSource.DEX];
    let sourceName: TDistributorSource = EDistributorSource.GENERAL;

    if (source === ECustomerSource.DEX) {
      paymentMethods = dex;
      selectedDetail = detail.generalCart;
      creditLimit = creditLimits[ECustomerSource.DEX];
    } else if (source === ECustomerSource.APUDEX) {
      paymentMethods = apudex;
      selectedDetail = detail.generalCart;
      creditLimit = creditLimits[ECustomerSource.APUDEX];
    } else if (source === ECustomerSource.HYBRID) {
      const isViewDex = sourceInView === ECustomerSource.DEX;
      sourceName = isViewDex ? EDistributorSource.ALICORP : EDistributorSource.ALLIED;
      selectedDetail = isViewDex ? detail.alicorpCart : detail.alliedCart;
      paymentMethods = isViewDex ? dex : apudex;
      creditLimit = isViewDex ? creditLimits[ECustomerSource.DEX] : creditLimits[ECustomerSource.APUDEX];
    }

    return {
      sourceName,
      detail: selectedDetail,
      paymentMethods,
      creditLimit,
      currency: extractCurrency(selectedDetail),
      hasCreditInsufficiency: hasCreditInsufficiency(creditLimit, selectedDetail),
      canPurchase: !detail.isPurchaseBlocked,
    };
  },
);

export const hasAcceptedlegalConsentSelector = createSelector(
  selectCustomerLegalConsent,
  selectCheckoutAgreements,
  (legalConsent, agreements) => {
    if (legalConsent) {
      return true;
    }

    return agreements.legalConsent;
  },
);

const paymentMethodsSelectors = createSelector(
  paymentMethodSelector,
  paymentMethodsBySourceSelector,
  (paymentMethod, paymentMethodsBySource) => ({ paymentMethod, paymentMethodsBySource }),
);

export const canEnableConfirmSubmitSelector = createSelector(
  authCustomerIsHybridSelector,
  authGetCustomerSource,
  checkoutDetailSelector,
  hasProductsOfSelector,
  canBuySelector,
  hasAcceptedlegalConsentSelector,
  paymentMethodsSelectors,
  (isHybrid, source, detail, hasProductsOf, canBuy, hasAcceptedlegalConsent, paymentMethods) => {
    const { paymentMethod, paymentMethodsBySource } = paymentMethods;
    if (isHybrid) {
      if (canBuy) {
        const canBuyAlicorp = canBuyByDistributor({
          method: paymentMethod.alicorpCart,
          hasProducts: hasProductsOf.alicorpCart,
          paymentMethods: paymentMethodsBySource.dex,
          isPurchaseBlocked: detail.isPurchaseBlocked,
        });

        const canBuyAllied = canBuyByDistributor({
          method: paymentMethod.alliedCart,
          hasProducts: hasProductsOf.alliedCart,
          paymentMethods: paymentMethodsBySource.apudex,
          isPurchaseBlocked: detail.isPurchaseBlocked,
        });

        return canBuyAlicorp && canBuyAllied && hasAcceptedlegalConsent;
      }
      return false;
    }

    const canBuyGeneral =
      canBuy &&
      canBuyByDistributor({
        method: paymentMethod.generalCart,
        hasProducts: hasProductsOf.generalCart,
        paymentMethods: source === ECustomerSource.DEX ? paymentMethodsBySource.dex : paymentMethodsBySource.apudex,
        isPurchaseBlocked: detail.isPurchaseBlocked,
      }) &&
      hasAcceptedlegalConsent;

    return canBuyGeneral;
  },
);
